import { useState } from "react";
import { Link } from "react-router-dom";
import { Star, Eye, FileText, Download } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import type { AccountOrderRow } from "@/types/account.ui";
import {
  DataTable,
  PageHeader,
  useDataTable,
  type DataTableColumn,
  type DataTableFilterConfig,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import { useAccountOrders } from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";
import { TaxInvoiceModal } from "@/components/account/TaxInvoiceModal";

const statusColor: Record<string, string> = {
  placed: "bg-info/10 text-info border-info/20",
  confirmed: "bg-warning/10 text-warning border-warning/20",
  dispatched: "bg-primary/10 text-primary border-primary/20",
  delivered: "bg-success/10 text-success border-success/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

const statusFilters: DataTableFilterConfig[] = [
  {
    id: "status",
    placeholder: "Status",
    options: [
      { label: "All Orders", value: "all" },
      { label: "Placed", value: "placed" },
      { label: "Dispatched", value: "dispatched" },
      { label: "Delivered", value: "delivered" },
      { label: "Cancelled", value: "cancelled" },
    ],
  },
];

const AccountOrders = () => {
  const [ratingOrderId, setRatingOrderId] = useState<string | null>(null);
  const [invoiceOrderId, setInvoiceOrderId] = useState<number | null>(null);
  const [rating, setRating] = useState(5);
  const { toast } = useToast();
  const { data, isLoading } = useAccountOrders();
  const orders = data?.items ?? [];

  const columns: DataTableColumn<AccountOrderRow>[] = [
    {
      id: "orderId",
      header: "Order",
      cell: (row) => (
        <Link to={`/account/orders/${row.id}`} className="text-primary hover:underline font-semibold">
          {row.orderId}
        </Link>
      ),
    },
    { id: "storeName", header: "Store", accessorKey: "storeName" },
    { id: "status", header: "Status", accessorKey: "status" },
    { id: "total", header: "Total", accessorKey: "total", align: "right" },
    {
      id: "actions",
      header: "Invoice",
      align: "right",
      cell: (row) => (
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1 text-xs font-semibold text-primary border-primary/30 hover:bg-primary hover:text-white"
          onClick={() => setInvoiceOrderId(Number(row.id))}
        >
          <FileText className="w-3.5 h-3.5" /> Tax Invoice
        </Button>
      ),
    },
  ];

  const table = useDataTable<AccountOrderRow>({
    data: orders,
    searchKeys: ["orderId", "storeName"],
    filterConfigs: statusFilters,
    filterPredicate: (row, state) =>
      state.filters.status === "all" || row.status === state.filters.status,
  });

  const renderOrderCard = (order: AccountOrderRow) => (
    <Card className="border border-border/70 shadow-xs hover:shadow-md transition-all rounded-2xl overflow-hidden bg-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-bold text-foreground font-heading">{order.orderId}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{order.storeName} • {order.orderDate}</p>
          </div>
          <Badge variant="outline" className={`px-2.5 py-0.5 text-xs font-semibold capitalize ${statusColor[order.status]}`}>{order.status}</Badge>
        </div>
        <div className="text-xs font-medium text-muted-foreground">
          {order.itemCount} item{order.itemCount !== 1 ? "s" : ""}
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-border/60">
          <span className="text-xs font-medium text-muted-foreground">
            {order.paymentMethod === "cod"
              ? "COD"
              : order.paymentMethod === "wallet"
                ? "Wallet"
                : "Razorpay"}{" "}
            • {order.deliveryType === "store" ? "Store delivery" : "Platform delivery"}
          </span>
          <span className="font-heading font-bold text-base text-foreground">₹{order.total}</span>
        </div>
        <div className="flex gap-2 pt-1 flex-wrap sm:flex-nowrap">
          <Button size="sm" className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm font-semibold rounded-xl gap-1.5 h-10 min-w-[120px]" asChild>
            <Link to={`/account/orders/${order.id}`}>
              <Eye className="w-4 h-4" /> Track Details
            </Link>
          </Button>
          <Button
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl gap-1.5 h-10 px-3.5 transition-colors shadow-xs"
            onClick={() => setInvoiceOrderId(Number(order.id))}
          >
            <FileText className="w-4 h-4" /> Invoice
          </Button>
          {order.status === "delivered" && (
            <Button size="sm" className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30 hover:bg-amber-500 hover:text-white font-semibold rounded-xl gap-1.5 h-10 transition-colors px-3.5" onClick={() => setRatingOrderId(order.id)}>
              <Star className="w-4 h-4 fill-amber-400 text-amber-400" /> Rate
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <PageHeader title="My Orders" description="Track and manage your orders and download tax invoices." />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={table.rows}
          getRowId={(row) => row.id}
          viewMode="cards"
          search={table.state.search}
          onSearchChange={table.setSearch}
          searchPlaceholder="Search orders..."
          filters={statusFilters}
          filterValues={table.state.filters}
          onFilterChange={table.setFilter}
          pagination={table.pagination}
          onPageChange={table.setPage}
          onPageSizeChange={table.setPageSize}
          renderMobileCard={renderOrderCard}
          cardClassName="border-0 shadow-none bg-transparent"
          className="space-y-4"
        />
      )}

      <TaxInvoiceModal
        orderId={invoiceOrderId}
        open={Boolean(invoiceOrderId)}
        onOpenChange={(open) => !open && setInvoiceOrderId(null)}
      />

      <Dialog open={Boolean(ratingOrderId)} onOpenChange={(open) => !open && setRatingOrderId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rate Your Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Label>Rating</Label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)}>
                  <Star className={`h-6 w-6 ${n <= rating ? "fill-warning text-warning" : "text-muted-foreground"}`} />
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingOrderId(null)}>Cancel</Button>
            <Button onClick={() => { toast({ title: "Thank you for your rating!" }); setRatingOrderId(null); }}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountOrders;
